import type { WireMessage } from "./agentMemory";
import { skippedToolReply } from "./agentMemory";

// A legitimate system prompt only ever sits at index 0. A trailing system message is an ephemeral
// leak (e.g. a page-context message that was never meant to persist) — drop it before balancing.
const withoutTrailingAnomaly = (messages: WireMessage[]): WireMessage[] => {
  const last = messages[messages.length - 1];
  return messages.length > 1 && last?.role === "system" ? messages.slice(0, -1) : messages;
};

const danglingCallIds = (messages: WireMessage[]): string[] => {
  const answered = new Set(
    messages.filter((m) => m.role === "tool" && m.toolCallId).map((m) => m.toolCallId as string),
  );
  const dangling: string[] = [];
  for (const message of messages) {
    if (message.role !== "assistant" || !message.toolCalls) {
      continue;
    }
    for (const call of message.toolCalls) {
      if (!answered.has(call.id)) {
        dangling.push(call.id);
      }
    }
  }
  return dangling;
};

// Turn a stored conversation into one that is safe to continue. Enforces the balance invariant —
// every assistant tool call must be followed by exactly one tool reply — by appending an aborted
// SKIPPED reply for any call left dangling (a mid-flight save, or a confirmation that was pending at
// reload), in call order: never resurrected, never reordered, so nothing auto-executes after a reload
// (ADR-029 flow E). The result is always a fresh, mutable copy: the input is RTK's cache, which is
// frozen in dev builds, and the adopted array becomes live memory that push/initSystem mutate.
export const toResumableConversation = (
  messages: WireMessage[],
): { messages: WireMessage[]; amended: boolean } => {
  const trimmed = withoutTrailingAnomaly(messages);
  const dangling = danglingCallIds(trimmed);
  const mutable = trimmed.map((message) => ({ ...message }));
  if (trimmed === messages && dangling.length === 0) {
    return { messages: mutable, amended: false };
  }
  return { messages: [...mutable, ...dangling.map(skippedToolReply)], amended: true };
};
